import { SETTINGS_KEYS } from '../config/environment'
import { DisplayMode, Document, FilterRule, SavedView } from '../types'
import { paramsFromViewState, paramsToViewState } from '../utils/queryParams'
import { apiService } from './api.service'
import { documentService } from './document.service'
import { settingsService } from './settings.service'

export enum DisplayFieldEnum {
  ASN = 'asn',
  CORRESPONDENT = 'correspondent',
  TITLE = 'title',
  TAGS = 'tag',
  OWNER = 'owner',
  NOTES = 'note',
  DOCUMENT_TYPE = 'documenttype',
  WORKFLOW_STATUS = 'workflowstatus',
  STORAGE_PATH = 'storagepath',
  CREATED = 'created',
  ADDED = 'added',
  PAGE_COUNT = 'pagecount',
  SHARED = 'shared',
  CUSTOM_FIELD = 'custom_field_',
}

export { DisplayMode }

export interface SortField {
  field: string
  name: string
}

export interface SelectionDataItem {
  id: number
  document_count: number
}

export interface SelectionData {
  selected_correspondents: SelectionDataItem[]
  selected_tags: SelectionDataItem[]
  selected_document_types: SelectionDataItem[]
  selected_workflow_statuses?: SelectionDataItem[]
  selected_storage_paths: SelectionDataItem[]
  selected_custom_fields: SelectionDataItem[]
}

export const DEFAULT_DISPLAY_FIELDS: DisplayFieldEnum[] = [
  DisplayFieldEnum.ASN,
  DisplayFieldEnum.CORRESPONDENT,
  DisplayFieldEnum.TITLE,
  DisplayFieldEnum.TAGS,
  DisplayFieldEnum.OWNER,
  DisplayFieldEnum.DOCUMENT_TYPE,
  DisplayFieldEnum.WORKFLOW_STATUS,
  DisplayFieldEnum.STORAGE_PATH,
  DisplayFieldEnum.CREATED,
  DisplayFieldEnum.PAGE_COUNT,
]

export function migrateDisplayFields(fields: string[]): DisplayFieldEnum[] {
  const fieldMap: { [key: string]: DisplayFieldEnum } = {
    tags: DisplayFieldEnum.TAGS,
    tag: DisplayFieldEnum.TAGS,
    document_type: DisplayFieldEnum.DOCUMENT_TYPE,
    documenttype: DisplayFieldEnum.DOCUMENT_TYPE,
    workflow_status: DisplayFieldEnum.WORKFLOW_STATUS,
    workflowstatus: DisplayFieldEnum.WORKFLOW_STATUS,
    storage_path: DisplayFieldEnum.STORAGE_PATH,
    storagepath: DisplayFieldEnum.STORAGE_PATH,
    notes: DisplayFieldEnum.NOTES,
    note: DisplayFieldEnum.NOTES,
    page_count: DisplayFieldEnum.PAGE_COUNT,
    pagecount: DisplayFieldEnum.PAGE_COUNT,
    asn: DisplayFieldEnum.ASN,
    correspondent: DisplayFieldEnum.CORRESPONDENT,
    title: DisplayFieldEnum.TITLE,
    owner: DisplayFieldEnum.OWNER,
    created: DisplayFieldEnum.CREATED,
    added: DisplayFieldEnum.ADDED,
    shared: DisplayFieldEnum.SHARED,
  }

  return fields
    .filter((field) => field !== 'custom_field' && field !== 'custom_field_')
    .map((field) => {
      if (field.startsWith('custom_field_') && field.length > 'custom_field_'.length) {
        return field as DisplayFieldEnum
      }
      return fieldMap[field] || (field as DisplayFieldEnum)
    })
}

export const DEFAULT_SORT_FIELDS: SortField[] = [
  { field: 'archive_serial_number', name: 'ASN' },
  { field: 'correspondent__name', name: 'Correspondent' },
  { field: 'title', name: 'Title' },
  { field: 'tags__name', name: 'Tags' },
  { field: 'owner', name: 'Owner' },
  { field: 'document_type__name', name: 'Document Type' },
  { field: 'workflow_status__name', name: 'Workflow Status' },
  { field: 'storage_path__name', name: 'Storage Path' },
  { field: 'created', name: 'Created' },
  { field: 'added', name: 'Added' },
  { field: 'page_count', name: 'Pages' },
]

export const FULLTEXT_SORT_FIELDS: SortField[] = [
  { field: 'archive_serial_number', name: 'ASN' },
  { field: 'correspondent__name', name: 'Correspondent' },
  { field: 'title', name: 'Title' },
  { field: 'tags__name', name: 'Tags' },
  { field: 'content', name: 'Content' },
  { field: 'owner', name: 'Owner' },
  { field: 'document_type__name', name: 'Document Type' },
  { field: 'workflow_status__name', name: 'Workflow Status' },
  { field: 'storage_path__name', name: 'Storage Path' },
  { field: 'created', name: 'Created' },
  { field: 'added', name: 'Added' },
  { field: 'page_count', name: 'Pages' },
]

class DocumentListViewService {
  private static readonly VIEW_CONFIG_STORAGE_KEY_PREFIX = 'document-list-service:currentViewConfig:'

  documents: Document[] = []
  selected = new Set<number>()
  private currentOrganizationId: number | null = null
  private _filterRules: FilterRule[] = []
  private _sortField = 'created'
  private _sortReverse = true
  currentPage = 1
  private _pageSize?: number
  collectionSize = 0

  private navigateCallback?: (params: Record<string, string | number | undefined>) => void
  private _displayMode: DisplayMode = DisplayMode.SMALL_CARDS
  private _displayFields: DisplayFieldEnum[] = [...DEFAULT_DISPLAY_FIELDS]
  /** Filter: only documents assigned to me (workflow tasks – stavka 14) */
  private _assignedToMe = false
  selectionData?: SelectionData = undefined
  /**
   * Monotonic counter incremented every time we kick off a new
   * selection_data fetch. Late-arriving fetches whose token no longer matches
   * are discarded so they can't overwrite newer state.
   */
  private _selectionDataToken = 0

  setOrganizationContext(organizationId: number | null): void {
    if (this.currentOrganizationId === organizationId) return

    this.currentOrganizationId = organizationId
    this.resetForOrganization()
    this.loadFromLocalStorage()
    this.notifyListeners()
  }

  private getViewConfigStorageKey(): string | null {
    if (typeof this.currentOrganizationId !== 'number') return null
    return `${DocumentListViewService.VIEW_CONFIG_STORAGE_KEY_PREFIX}${this.currentOrganizationId}`
  }

  private loadFromLocalStorage(): void {
    const storageKey = this.getViewConfigStorageKey()
    if (!storageKey) return

    const savedStateJson = localStorage.getItem(storageKey)

    if (savedStateJson) {
      try {
        const savedState = JSON.parse(savedStateJson)

        if (savedState.displayFields && Array.isArray(savedState.displayFields)) {
          this._displayFields = migrateDisplayFields(savedState.displayFields)
        }
        if (savedState.displayMode) {
          this._displayMode = savedState.displayMode
        }
        if (savedState.sortField) {
          this._sortField = savedState.sortField
        }
        if (savedState.sortReverse !== undefined) {
          this._sortReverse = savedState.sortReverse
        }
        if (savedState.filterRules && Array.isArray(savedState.filterRules)) {
          this._filterRules = savedState.filterRules
        }
      } catch (e) {
        localStorage.removeItem(storageKey)
      }
    }
  }

  get displayMode(): DisplayMode {
    return this._displayMode
  }

  set displayMode(mode: DisplayMode) {
    this._displayMode = mode
    this.saveDocumentListView()
    this.notifyListeners()
  }

  get displayFields(): DisplayFieldEnum[] {
    return this._displayFields
  }

  set displayFields(fields: DisplayFieldEnum[]) {
    this._displayFields = fields
    this.saveDocumentListView()
    this.notifyListeners()
  }

  get filterRules(): FilterRule[] {
    return this._filterRules
  }

  set filterRules(rules: FilterRule[]) {
    this._filterRules = rules
    this.saveDocumentListView()
    this.notifyListeners()
  }

  private saveDocumentListView(): void {
    if (this.activeSavedViewId == null) {
      const storageKey = this.getViewConfigStorageKey()
      if (!storageKey) return

      const savedState = {
        collectionSize: this.collectionSize,
        currentPage: this.currentPage,
        filterRules: this._filterRules,
        sortField: this._sortField,
        sortReverse: this._sortReverse,
        displayMode: this._displayMode,
        displayFields: this._displayFields,
      }
      localStorage.setItem(storageKey, JSON.stringify(savedState))
    }
  }

  isReloading = false
  error: string | null = null

  activeSavedViewId: number | null = null
  activeSavedViewTitle: string | null = null

  private abortController: AbortController | null = null

  private reloadTimeout: NodeJS.Timeout | null = null

  private updateTrigger = 0
  private listeners: Set<() => void> = new Set()

  subscribe(callback: () => void): () => void {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  }

  private notifyListeners(): void {
    this.listeners.forEach((callback) => callback())
  }

  get sortFields(): SortField[] {
    return DEFAULT_SORT_FIELDS
  }

  get sortFieldsFullText(): SortField[] {
    return FULLTEXT_SORT_FIELDS
  }

  get pageSize(): number {
    return this._pageSize ?? settingsService.get(SETTINGS_KEYS.DOCUMENT_LIST_SIZE) ?? 50
  }

  get getLastPage(): number {
    return Math.ceil(this.collectionSize / this.pageSize)
  }

  private triggerUpdate(): void {
    this.updateTrigger++
    this.notifyListeners()
  }

  get updateCount(): number {
    return this.updateTrigger
  }

  isSelected(document: Document): boolean {
    return this.selected.has(document.id)
  }

  toggleSelected(document: Document): void {
    if (this.selected.has(document.id)) {
      this.selected.delete(document.id)
    } else {
      this.selected.add(document.id)
    }
    this.notifyListeners()
  }

  selectRangeTo(document: Document): void {
    if (this.selected.size > 0) {
      const firstSelected = Math.min(...Array.from(this.selected))
      const currentId = document.id
      const start = Math.min(firstSelected, currentId)
      const end = Math.max(firstSelected, currentId)

      for (let i = 0; i < this.documents.length; i++) {
        const doc = this.documents[i]
        if (doc.id >= start && doc.id <= end) {
          this.selected.add(doc.id)
        }
      }
    }
    this.notifyListeners()
  }

  selectNone(): void {
    this.selected.clear()
    this.notifyListeners()
  }

  async selectAll(): Promise<void> {
    try {
      const ids = await documentService.listAllFilteredIds(this.filterRules)
      ids.forEach((id) => this.selected.add(id))
      this.notifyListeners()
    } catch (error) {}
  }

  selectPage(): void {
    this.selected.clear()
    this.documents.forEach((doc) => this.selected.add(doc.id))
    this.notifyListeners()
  }

  setNavigateCallback(callback: ((params: Record<string, string | number | undefined>) => void) | null): void {
    this.navigateCallback = callback || undefined
  }

  get assignedToMe(): boolean {
    return this._assignedToMe
  }

  setAssignedToMe(value: boolean): void {
    if (this._assignedToMe !== value) {
      this._assignedToMe = value
      this.currentPage = 1
      this.saveDocumentListView()
      this.notifyListeners()
    }
  }

  updateQueryParams(pageOnly: boolean = false): void {
    if (this.navigateCallback && !this.activeSavedViewId) {
      const params = paramsFromViewState(this.filterRules, this.sortField, this.sortReverse, this.currentPage, pageOnly)
      if (this._assignedToMe) {
        params.assigned_to_me = 'true'
      }
      this.navigateCallback(params)
    }
  }

  quickFilter(rules: FilterRule[]): void {
    this.activeSavedViewId = null
    this.activeSavedViewTitle = null
    this._filterRules = [...rules]
    this.currentPage = 1
    this.saveDocumentListView()
    this.reload()
  }

  setSort(field: string, reverse: boolean): void {
    this._sortField = field
    this._sortReverse = reverse
    this.currentPage = 1
    this.saveDocumentListView()
    this.reload()
  }

  get sortField(): string {
    return this._sortField
  }

  set sortField(field: string) {
    this._sortField = field
    this.currentPage = 1
    this.saveDocumentListView()
    this.reload()
  }

  get sortReverse(): boolean {
    return this._sortReverse
  }

  set sortReverse(reverse: boolean) {
    this._sortReverse = reverse
    this.currentPage = 1
    this.saveDocumentListView()
    this.updateQueryParams()
    this.reload()
  }

  activateSavedView(view: SavedView | null): void {
    if (view) {
      this.activeSavedViewId = view.id || null
      this.activeSavedViewTitle = view.name || null
      this._filterRules = view.filter_rules || []
      this._sortField = view.sort_field || 'created'
      this._sortReverse = view.sort_reverse ?? true
      this._pageSize = view.page_size ?? undefined
      this._displayMode = view.display_mode || DisplayMode.SMALL_CARDS
      this._displayFields =
        this.convertDisplayFields(view.display_fields) || [...DEFAULT_DISPLAY_FIELDS]
      this.notifyListeners()
    } else {
      this.activeSavedViewId = null
      this.activeSavedViewTitle = null
      this._pageSize = undefined
      this.notifyListeners()
    }
  }

  activateSavedViewWithQueryParams(view: SavedView, queryParams: any): void {
    this.activateSavedView(view)

    if (queryParams.get('page')) {
      this.currentPage = parseInt(queryParams.get('page'))
    }
    if (queryParams.get('page_size')) {
      this._pageSize = parseInt(queryParams.get('page_size'))
    }
    if (queryParams.get('sort')) {
      this._sortField = queryParams.get('sort')
    }
    if (queryParams.get('reverse')) {
      this._sortReverse = queryParams.get('reverse') === 'true'
    }
  }

  private convertDisplayFields(fields?: any[] | string[]): DisplayFieldEnum[] | undefined {
    if (!fields || fields.length === 0) return undefined

    if (typeof fields[0] === 'string') {
      return migrateDisplayFields(fields as string[])
    }

    const stringFields = fields.map((f: any) => f.id || f) as string[]
    return migrateDisplayFields(stringFields)
  }

  loadFromQueryParams(queryParams: any): void {
    this._assignedToMe = queryParams.get('assigned_to_me') === 'true'

    const paramsEmpty = queryParams.toString() === '' || queryParams.toString() === 'page=1' || queryParams.toString() === ''

    if (paramsEmpty) {
    } else {
      const state = paramsToViewState(queryParams)

      this._filterRules = state.filterRules
      this._sortField = state.sortField
      this._sortReverse = state.sortReverse
      this.currentPage = state.currentPage
      this._assignedToMe = state.assignedToMe

      if (queryParams.get('page_size')) {
        this._pageSize = parseInt(queryParams.get('page_size'))
      }
    }
  }

  hasNext(docId: number): boolean {
    if (!this.documents || this.documents.length === 0) return false
    const index = this.documents.findIndex((d) => d.id === docId)
    if (index === -1) return false

    if (index + 1 < this.documents.length) return true

    return this.currentPage < this.getLastPage
  }

  hasPrevious(docId: number): boolean {
    if (!this.documents || this.documents.length === 0) return false
    const index = this.documents.findIndex((d) => d.id === docId)
    if (index === -1) return false

    return !(index === 0 && this.currentPage === 1)
  }

  async getNext(currentDocId: number): Promise<number | null> {
    if (!this.documents || this.documents.length === 0) return null

    const index = this.documents.findIndex((d) => d.id === currentDocId)
    if (index === -1) return null

    if (index + 1 < this.documents.length) {
      return this.documents[index + 1].id
    }

    if (this.currentPage < this.getLastPage) {
      const previousPage = this.currentPage
      this.currentPage += 1
      await this.reload()
      if (this.documents.length > 0) {
        return this.documents[0].id
      }
      this.currentPage = previousPage
    }

    return null
  }

  async getPrevious(currentDocId: number): Promise<number | null> {
    if (!this.documents || this.documents.length === 0) return null

    const index = this.documents.findIndex((d) => d.id === currentDocId)
    if (index === -1) return null

    if (index > 0) {
      return this.documents[index - 1].id
    }

    if (this.currentPage > 1) {
      const previousPage = this.currentPage
      this.currentPage -= 1
      await this.reload()
      if (this.documents.length > 0) {
        return this.documents[this.documents.length - 1].id
      }
      this.currentPage = previousPage
    }

    return null
  }

  async reload(callback?: () => void): Promise<void> {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout)
    }

    return new Promise((resolve, reject) => {
      this.reloadTimeout = setTimeout(async () => {
        try {
          await this._performReload(callback)
          resolve()
        } catch (error) {
          reject(error)
        }
      }, 100)
    })
  }

  private async _performReload(callback?: () => void): Promise<void> {
    this.cancelPending()

    this.isReloading = true
    this.error = null

    this.abortController = new AbortController()

    const extraParams: Record<string, unknown> = {
      truncate_content: true,
      // Avoid serialising every matching document id in the list JSON; filter
      // sidebar aggregates load via GET /documents/selection_summary/ instead.
      include_all: 'false',
    }
    if (this._assignedToMe) {
      extraParams.assigned_to_me = 'true'
    }
    try {
      const result = await documentService.listFiltered(this.currentPage, this.pageSize, this.sortField, this.sortReverse, this.filterRules, extraParams)

      this.documents = result.results
      this.collectionSize = result.count
      // Clear stale counts immediately so consumers don't display data from
      // the previous filter while the new selection summary call is in flight.
      this.selectionData = undefined

      this.updateQueryParams()

      if (callback) callback()

      void this._loadSelectionSummaryInBackground()
    } catch (error: any) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        this.error = error.response?.data?.detail || error.message || 'Error loading documents'
        this.documents = []
        this.collectionSize = 0
      }
    } finally {
      this.isReloading = false
      this.abortController = null
      this.triggerUpdate()
    }
  }

  private async _loadSelectionSummaryInBackground(): Promise<void> {
    const token = ++this._selectionDataToken
    try {
      const summaryExtra: Record<string, unknown> = {}
      if (this._assignedToMe) {
        summaryExtra.assigned_to_me = 'true'
      }
      const selectionData = await documentService.getSelectionSummary(
        this.currentPage,
        this.pageSize,
        this.sortField,
        this.sortReverse,
        this.filterRules,
        summaryExtra,
      )
      if (token !== this._selectionDataToken) return
      this.selectionData = selectionData
    } catch {
      if (token !== this._selectionDataToken) return
      this.selectionData = undefined
    } finally {
      if (token === this._selectionDataToken) {
        this.triggerUpdate()
      }
    }
  }

  cancelPending(): void {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout)
      this.reloadTimeout = null
    }
    // Invalidate any in-flight selection_data fetch from a previous reload
    // so its result can't overwrite the new state once it eventually
    // resolves.
    this._selectionDataToken++
  }

  // Reset service state
  reset(): void {
    this.currentOrganizationId = null
    this.resetForOrganization()
    this.notifyListeners()
  }

  private resetForOrganization(): void {
    this.documents = []
    this.selected.clear()
    this._filterRules = []
    this._sortField = 'created'
    this._sortReverse = true
    this.currentPage = 1
    this._pageSize = undefined
    this.collectionSize = 0
    this._displayMode = DisplayMode.SMALL_CARDS
    this._displayFields = [...DEFAULT_DISPLAY_FIELDS]
    this._assignedToMe = false
    this.selectionData = undefined
    this.isReloading = false
    this.error = null
    this.activeSavedViewId = null
    this.activeSavedViewTitle = null
    this.cancelPending()
  }

  async bulkEdit(documentIds: number[], data: any): Promise<void> {
    await apiService.patch('documents/bulk_edit/', {
      documents: documentIds,
      ...data,
    })
  }

  async bulkDelete(documentIds: number[]): Promise<void> {
    await apiService.delete('documents/bulk_edit/', {
      data: { documents: documentIds },
    })
  }
}

export const documentListViewService = new DocumentListViewService()
